<script setup>
import {
  CallIncoming01Icon,
  PanelRightCloseIcon,
  Tag01Icon,
  Timer02Icon,
  UserSquareIcon,
} from '@hugeicons/core-free-icons';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import { computed, ref } from 'vue';
import ConversationAttachmentsPanel from './ConversationAttachmentsPanel.vue';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { vOnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();
const breakpoints = useBreakpoints(breakpointsTailwind);
const isMdUp = breakpoints.greaterOrEqual('md');
const activeQuickAction = ref('contact');
const activitiesCopy = {
  title: 'Activities',
  allTime: 'All Time',
  today: 'Today',
  unassigned: 'Unassigned',
  lifecycle: 'Lifecycle',
  timestamp: 'May 09, 2026 06:59 AM',
  commentPlaceholder: 'Use @ to mention a teammate. Comment...',
};
const activityItems = [
  'You unassigned this person',
  'Cold Lead updated to New Lead by you',
  'Customer updated to Cold Lead by you',
  'New Lead updated to Customer by you',
];

const quickActionItems = [
  { key: 'contact', icon: UserSquareIcon },
  { key: 'call', icon: CallIncoming01Icon },
  { key: 'attachments', icon: Tag01Icon },
  { key: 'activities', icon: Timer02Icon },
];

const isSidebarOpen = computed(() => {
  return !!(props.currentChat?.id && uiSettings.value?.is_contact_sidebar_open);
});

const sidebarClasses = computed(() => {
  return isSidebarOpen.value
    ? 'w-full max-w-sm ltr:right-0 rtl:left-0 ltr:border-l rtl:border-r md:w-[360px] md:min-w-[360px] 2xl:w-[400px] 2xl:min-w-[400px]'
    : 'w-12 min-w-12 max-w-[48px] ltr:right-0 rtl:left-0 ltr:border-l rtl:border-r';
});

const activeQuickActionTitle = computed(() => {
  const titleMap = {
    contact: t('CONVERSATION.SIDEBAR.TAB_TITLES.CONTACT'),
    call: t('CONVERSATION.SIDEBAR.TAB_TITLES.CALL'),
    attachments: t('CONVERSATION.SIDEBAR.TAB_TITLES.ATTACHMENTS'),
    activities: t('CONVERSATION.SIDEBAR.TAB_TITLES.ACTIVITIES'),
  };

  return titleMap[activeQuickAction.value] || '';
});

const closeContactPanel = () => {
  if (uiSettings.value?.is_contact_sidebar_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
  }
};

/** On md+ the sidebar is static beside the thread; clicks there must not collapse the panel. */
const onContactSidebarClickOutside = () => {
  if (isMdUp.value) return;
  closeContactPanel();
};

const handleQuickActionClick = key => {
  if (isSidebarOpen.value && activeQuickAction.value === key) {
    closeContactPanel();
    return;
  }

  activeQuickAction.value = key;

  if (!isSidebarOpen.value) {
    updateUISettings({
      is_contact_sidebar_open: true,
      is_copilot_panel_open: false,
    });
  }
};
</script>

<template>
  <div
    v-on-click-outside="onContactSidebarClickOutside"
    class="absolute top-0 z-40 flex h-full shrink-0 overflow-hidden border-n-weak bg-n-surface-2 shadow-lg transition-all duration-300 ease-in-out ltr:right-0 rtl:left-0 md:static md:shadow-none"
    :class="sidebarClasses"
  >
    <div
      v-if="isSidebarOpen"
      class="flex min-w-0 flex-1 flex-col border-r border-n-weak bg-n-surface-1"
    >
      <div class="flex h-[41px] items-center border-b border-n-weak px-3">
        <div class="flex h-7 w-full items-center gap-2">
          <button
            type="button"
            class="flex size-7 items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2"
            :aria-label="$t('GENERAL.CLOSE')"
            @click="closeContactPanel"
          >
            <HugeIcon
              :icon="PanelRightCloseIcon"
              :size="20"
              class="size-5 shrink-0"
            />
          </button>
          <span
            class="min-w-0 truncate text-base font-semibold leading-7 text-n-slate-12"
          >
            {{ activeQuickActionTitle }}
          </span>
        </div>
      </div>

      <div class="min-h-0 flex-1 overflow-auto">
        <ContactPanel v-if="activeQuickAction === 'contact'" />
        <ConversationAttachmentsPanel
          v-else-if="activeQuickAction === 'attachments'"
          :current-chat="currentChat"
        />
        <div
          v-else-if="activeQuickAction === 'call'"
          class="flex h-full w-full items-center justify-center bg-n-surface-1 p-6"
        >
          <div
            class="flex flex-col items-center gap-3 rounded-2xl border border-n-weak bg-n-alpha-1 px-6 py-8 text-center"
          >
            <HugeIcon
              :icon="CallIncoming01Icon"
              :size="24"
              class="shrink-0 text-n-slate-11"
            />
            <div>
              <p class="text-sm font-medium text-n-slate-12">
                {{ activeQuickActionTitle }}
              </p>
              <p class="text-sm text-n-slate-10">
                {{ $t('CONVERSATION.SIDEBAR.COMING_SOON') }}
              </p>
            </div>
          </div>
        </div>
        <div
          v-else-if="activeQuickAction === 'activities'"
          class="flex h-full w-full flex-col bg-n-surface-1"
        >
          <div class="flex-1 space-y-3 overflow-y-auto p-3">
            <button
              type="button"
              class="flex h-9 w-full items-center justify-center gap-2 rounded-md border border-n-weak text-sm text-n-slate-12"
            >
              <span class="i-lucide-calendar-days size-4" />
              <span>{{ activitiesCopy.allTime }}</span>
            </button>
            <div
              class="mx-auto w-fit rounded bg-n-slate-3 px-2 py-1 text-xs text-n-slate-11"
            >
              {{ activitiesCopy.today }}
            </div>
            <article
              v-for="item in activityItems"
              :key="item"
              class="rounded-lg border border-n-weak bg-n-surface-1 p-3 text-sm text-n-slate-12"
            >
              <div class="mb-3 flex items-center gap-2 font-medium">
                <span class="i-lucide-refresh-cw size-4" />
                <span>
                  {{
                    item.includes('unassigned')
                      ? activitiesCopy.unassigned
                      : activitiesCopy.lifecycle
                  }}
                </span>
              </div>
              <p class="border-t border-n-weak pt-3">{{ item }}</p>
              <p class="mt-3 text-xs text-n-slate-10">
                {{ activitiesCopy.timestamp }}
              </p>
            </article>
          </div>
          <div class="border-t border-n-weak p-3">
            <div class="rounded-lg border border-n-amber-7 bg-n-amber-2 p-3">
              <input
                class="mb-3 w-full bg-transparent text-sm outline-none placeholder:text-n-slate-10"
                :placeholder="activitiesCopy.commentPlaceholder"
              />
              <div class="flex items-center justify-between">
                <div class="flex gap-2 text-n-slate-11">
                  <span class="i-lucide-wand-sparkles size-4" />
                  <span class="i-lucide-smile size-4" />
                  <span class="i-lucide-at-sign size-4" />
                </div>
                <button
                  class="flex size-8 items-center justify-center rounded-md bg-n-slate-5 text-n-slate-11"
                  type="button"
                >
                  <span class="i-lucide-send size-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="flex w-12 shrink-0 flex-col items-center border-l border-n-weak bg-n-surface-1 px-1 py-2.5"
    >
      <div
        class="flex w-full flex-col items-center gap-2 rounded-xl border border-n-weak bg-n-alpha-1 p-1.5 shadow-sm"
      >
        <button
          v-for="item in quickActionItems"
          :key="item.key"
          type="button"
          class="flex size-8 items-center justify-center rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-11 shadow-sm transition-colors hover:border-n-slate-6 hover:bg-n-alpha-2"
          :class="{
            '!border-n-brand !bg-n-brand !text-white !shadow-none':
              isSidebarOpen && activeQuickAction === item.key,
          }"
          @click="handleQuickActionClick(item.key)"
        >
          <HugeIcon :icon="item.icon" :size="22" class="shrink-0" />
        </button>
      </div>
    </div>
  </div>
</template>
